import React, { useCallback, useEffect, useMemo } from "react";
import classNames from "classnames";
import ACLStore from "lib/aclStore";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import _ from "lodash";
import { useI18n } from "Hooks";
import {
  getCurriculumStandardsQuery,
  getLearningCourseStandardsQuery,
} from "Post/modules/PostQuery";
import {
  getPublishedSubjectStandardQueryVariables,
  getStandardsEdges,
  DEFAULT_STANDARD_FILTERS,
  DEFAULT_HIDDEN_COLUMNS,
  getIsParentStandardsTaggingEnabled,
  getIsStandardsV2,
} from "Post/utils";
import {
  getSelectedCurriculumId,
  getOrganizationId,
} from "modules/CommonSelectors";

import classes from "./StandardTable.scss";

import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";

import {
  DATA_GRID_CELL_TYPES,
  DATA_GRID_COLUMN_TYPES,
} from "StandardsTable/modules/constants";
import { Button, EmptyState, SearchInput } from "@toddle-edu/ds-web";

import { getAcademicSetupType } from "Teacher/routes/LearningCourses/modules/Utils";
import { COURSE_BASED_GRADING } from "Constants/stringConstants";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  NoSearchInFilterIllustration,
  NoServiceAsActionInsightsIllustration,
} from "@toddle-edu/ds-theme";

import StandardsTable from "StandardsTable";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import {
  useStandardsTableProps,
  useStandardsConfigureColumns,
} from "StandardsTable/hooks";
import { handleSelectionChange } from "StandardsTable/modules/Utils";
import ConfigureColumns from "AppComponents/StandardsSelectionModal/components/ConfigureColumns/ConfigureColumns";
import StandardFilter from "../StandardsFilter";
import {
  DATA_GRID_COLUMNS,
  NON_CONFIGURABLE_COLUMNS,
} from "AppComponents/StandardsSelectionModal/modules/Constants";
import {
  DATA_SET_COLUMNS_SETTING,
  ENABLE_STANDARDS_OUTLINE,
} from "Constants/permissionConstants";

const BLANK_OBJECT = {};
const BLANK_ARRAY = [];
const StandardTableView = props => {
  const {
    edges,
    standardFilters,
    setStandardFilters,
    setSelectedStandards = _.noop,
    selectedStandards,
    orgsSubjectAndTagsData,
    columns,
    searchText,
    setSearchText = _.noop,
    isLoading,
    learningCourseId,
    setAllStadardsRef = _.noop,
  } = props;
  const { t } = useI18n(["common", "journal"]);
  const hasEffectRun = React.useRef(false);

  useEffect(() => {
    if (!hasEffectRun.current && _.size(edges) > 0) {
      hasEffectRun.current = true;
      setAllStadardsRef(edges || []);
    }
  }, [edges, setAllStadardsRef]);

  const {
    columnIdsForConfigureModal,
    currentColumns,
    updateColumns,
  } = useStandardsConfigureColumns({
    settingName: DATA_SET_COLUMNS_SETTING,
    moduleName: "PortfolioStandards",
    defaultColumns: columns,
    nonConfigurableColumns: NON_CONFIGURABLE_COLUMNS,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
  });

  const handleSearchTextChange = useCallback(value => {
    setSearchText(value);
  }, []);

  const _getEdgesFromNodeMap = ({ nodeMap, selectedStandards }) => {
    return Array.from(nodeMap.values(), ({ rowId, children, columns }) => {
      if (
        !_.isEmpty(children) &&
        getIsStandardsV2() &&
        !getIsParentStandardsTaggingEnabled() &&
        !learningCourseId
      ) {
        _.set(columns, "[0].isDisabled", true);
      }
      columns[0].value = getCheckboxValue({
        value: selectedStandards.has(rowId),
      });

      return {
        rowId,
        children,
        columns,
      };
    });
  };

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectedStandards: new Set(selectedStandards || []),
      });
    },
    [selectedStandards, edges]
  );

  const handleSetSelectedRows = useCallback(
    rows => {
      setSelectedStandards(rows);
    },
    [setSelectedStandards]
  );

  const onCellEdit = useCallback(
    ({ params }) => {
      return handleSelectionChange({
        params,
        selectedRows: selectedStandards || [],
        setSelectedRows: rows => handleSetSelectedRows(Array.from(rows)),
      });
    },
    [handleSetSelectedRows, selectedStandards]
  );

  const getColumnProperties = t => {
    return {
      [DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS]: {
        headerConfig: {
          title: t("scopeAndSequence:tagged_in_label", {
            label: t("common:task_plural"),
          }),
          width: 176,
          tooltip: t("scopeAndSequence:tagged_in_label_header_tooltip", {
            label: t("common:task_plural"),
          }),
        },
        cellConfig: {
          cellType: DATA_GRID_CELL_TYPES.NUMBER,
          styles: {
            align: "right",
            color: "#008392",
          },
        },
        customConfig: {
          property: "standardCountWrapper.assessment.totalCount",
          showLoader: true,
        },
      },
    };
  };

  const { nodeMap, properties, sideNavData } = useStandardsTableProps({
    edges,
    columns: currentColumns,
    properties: getColumnProperties(t),
  });
  const memoizedEdges = useMemo(() => edges || [], [edges]);

  const resetFilters = useCallback(() => {
    setStandardFilters(DEFAULT_STANDARD_FILTERS);
  }, [setStandardFilters]);

  const getStandardSetEmptyStateSubtitle = useCallback(({ isAdmin, t }) => {
    if (isAdmin) {
      return t("journal:no_standards_set_added_subtext_admin");
    }
    return t("journal:no_standards_set_added_subtext_teacher");
  }, []);

  const resetSearch = useCallback(() => {
    setSearchText("");
    setStandardFilters(DEFAULT_STANDARD_FILTERS);
  }, [setSearchText, setStandardFilters]);

  const showOutline = ACLStore.can(ENABLE_STANDARDS_OUTLINE);

  const gridClasses = classNames(classes.gridBody, {
    [classes.outlineHiddenGridBody]: !showOutline,
  });

  return (
    <StandardsTable
      columns={currentColumns}
      nodeMap={nodeMap}
      properties={properties}
      sideNavData={sideNavData}
      showLoader={isLoading}
      showOutline={showOutline}
    >
      <StandardsTable.Body bodyClass={gridClasses}>
        <StandardsTable.Header>
          <div className={classes.filterContainer}>
            <div className={classes.searchStandardContainer}>
              <SearchInput
                value={searchText}
                onChange={handleSearchTextChange}
                placeholder={t("journal:search_for_a_standard")}
                focusSearchBySlash={true}
              />
            </div>
            <div className={classes.filterRightContainer}>
              <StandardFilter
                subjects={orgsSubjectAndTagsData?.subjects}
                grades={orgsSubjectAndTagsData?.grades}
                tags={orgsSubjectAndTagsData?.tags}
                setStandardFilters={setStandardFilters}
                standardFilters={
                  _.isEmpty(standardFilters)
                    ? DEFAULT_STANDARD_FILTERS
                    : standardFilters
                }
              />
              <ConfigureColumns
                currentColumns={currentColumns}
                updateColumns={updateColumns}
                columnIdsForConfigureModal={columnIdsForConfigureModal}
                properties={properties}
              />
            </div>
          </div>
        </StandardsTable.Header>
        {_.isEmpty(edges) && !isLoading ? (
          <div className={classes.emptyStateContainer}>
            <EmptyState
              illustration={
                searchText
                  ? NoSearchInFilterIllustration
                  : NoServiceAsActionInsightsIllustration
              }
              title={
                searchText
                  ? t("journal:no_results_for_keyword", { keyword: searchText })
                  : t("journal:no_standards_added_subtext")
              }
              subtitle={
                searchText
                  ? t("journal:please_reset_filters")
                  : getStandardSetEmptyStateSubtitle({
                      isAdmin: getIsAdminByRole(),
                      t,
                    })
              }
              primaryButton={
                <Button
                  onClick={resetSearch}
                  type={"outlined"}
                  variant={"neutral"}
                >
                  {t("common:reset_with_label", { label: t("common:filters") })}
                </Button>
              }
            />
          </div>
        ) : (
          <StandardsTable.Grid
            getEdgesFromNodeMap={getEdgesFromNodeMap}
            resetFilters={resetFilters}
            onCellEdit={onCellEdit}
            showLoader={isLoading}
          />
        )}
      </StandardsTable.Body>
    </StandardsTable>
  );
};

const mapStateToProps = state => {
  const learningCourseId = getSelectedLearningCourseId(state);
  const showCourseStandards = true;
  const organizationId = getOrganizationId(state);
  const { type: curriculumProgramType } =
    _.get(state, "platform.currentCurriculumProgram", BLANK_OBJECT) ||
    BLANK_OBJECT;
  const curriculumId = getSelectedCurriculumId(state);
  const academicSetupType = getAcademicSetupType();
  const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;

  const selectedGrades =
    state.teacher?.selected_class?.selected_grades || [
      _.get(state, "post.postDetails.course.grade.id", ""),
    ] ||
    BLANK_ARRAY;
  const columns = isCourseBasedGrading
    ? DATA_GRID_COLUMNS
    : _.without(DATA_GRID_COLUMNS, DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS);

  return {
    learningCourseId,
    showCourseStandards,
    organizationId,
    curriculumId,
    academicSetupType,
    columns,
    curriculumProgramType,
    selectedGrades,
    isPYPCurriculum: curriculumProgramType === "IB_PYP",
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getLearningCourseStandardsQuery, {
    name: "getLearningCourseStandards",
    options: ({ learningCourseId, selectedStandardSet, ...rest }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPublishedSubjectStandardQueryVariables({
        ...rest,
        id: learningCourseId,
        standardSetIds: selectedStandardSet ? [selectedStandardSet] : [],
      }),
    }),
    skip: ({ learningCourseId, showCourseStandards, showNewStandardsUIInV1 }) =>
      !learningCourseId ||
      !showCourseStandards ||
      (showNewStandardsUIInV1 && !learningCourseId),
    props({
      getLearningCourseStandards: { node, networkStatus },
      ownProps: { showCourseStandards, ...rest },
    }) {
      const edges = getStandardsEdges({ node, showCourseStandards });
      return {
        edges,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  graphql(getCurriculumStandardsQuery, {
    name: "getCurriculumStandards",
    options: ({
      curriculumId,
      curriculumProgramType,
      selectedStandardSet,
      selectedGrades,
      isPYPCurriculum,
      ...rest
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        isPYPCurriculum,
        id: selectedStandardSet,
        filters: {
          subjects: _.get(rest, "standardFilters.subjects", []),
          grades: selectedGrades,
          archivalState: "ACTIVE",
          tags: _.get(rest, "standardFilters.tags", []),
          searchText: _.get(rest, "searchText") || undefined,
        },
      },
    }),
    skip: ({ curriculumId, showNewStandardsUIInV1 }) =>
      !curriculumId || !showNewStandardsUIInV1,
    props({
      getCurriculumStandards: { node, networkStatus },
      ownProps: { showCourseStandards, isPYPCurriculum, ...rest },
    }) {
      const standardEdges =
        node?.standardSetWrapper?.publishedStandards?.edges || BLANK_ARRAY;
      const benchmarkEdges =
        node?.standardSetWrapper?.publishedBenchmarks?.edges || BLANK_ARRAY;
      const edges = isPYPCurriculum ? benchmarkEdges : standardEdges;
      return {
        edges,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  })
)(StandardTableView);
